import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Pencil, Trash2 } from "lucide-react";
import { API } from "@/data/campaign";

const inputCls =
  "w-full border border-pine-700/25 bg-paper px-4 py-3.5 text-base text-ink placeholder:text-ink/40 focus:border-terra-500 focus:outline-none";
const labelCls = "mb-1.5 block font-mono text-[10px] font-semibold uppercase tracking-[0.2em] text-ink/60";

const CATEGORIES = ["Traffic", "Development", "Taxes", "Voting", "Community", "Environment", "Housing", "Safety", "Transit", "Governance", "City Services"];
const POSITIONS = ["", "Strongly agree", "Somewhat agree", "About the same as now", "Somewhat disagree", "Strongly disagree"];

const EMPTY = { question: "", answer: "", category: "", position: "", sort_order: "", published: false };

const AdminQuestions = ({ headers }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/questions`, { headers });
      setItems(data.items);
    } catch {
      toast.error("Could not load questions.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const save = async () => {
    if (!form.question.trim() || !form.answer.trim()) {
      toast.error("Question and answer are required.");
      return;
    }
    setSaving(true);
    const payload = { ...form, sort_order: form.sort_order === "" ? null : Number(form.sort_order) };
    try {
      if (editId) {
        await axios.patch(`${API}/admin/questions/${editId}`, payload, { headers });
        toast.success("Question updated.");
      } else {
        await axios.post(`${API}/admin/questions`, payload, { headers });
        toast.success(payload.published ? "Question is live on /questions." : "Draft saved.");
      }
      setForm(EMPTY);
      setEditId(null);
      load();
    } catch {
      toast.error("Could not save the question.");
    } finally {
      setSaving(false);
    }
  };

  const edit = (q) => {
    setEditId(q.id);
    setForm({
      question: q.question,
      answer: q.answer,
      category: q.category || "",
      position: q.position || "",
      sort_order: q.sort_order ?? "",
      published: q.published,
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const remove = async (id) => {
    await axios.delete(`${API}/admin/questions/${id}`, { headers });
    toast.success("Question deleted.");
    load();
  };

  return (
    <div data-testid="questions-editor">
      <div className="border border-pine-700/15 bg-white/60 p-6 sm:p-8">
        <h3 className="font-serif text-xl font-medium tracking-tight">{editId ? "Edit question" : "Add a new question"}</h3>
        <div className="mt-5 space-y-4">
          <label className="block">
            <span className={labelCls}>Question *</span>
            <input data-testid="question-input" value={form.question} onChange={(e) => setForm((f) => ({ ...f, question: e.target.value }))} className={inputCls} placeholder="What are residents asking?" />
          </label>
          <label className="block">
            <span className={labelCls}>Answer *</span>
            <textarea rows={6} data-testid="answer-input" value={form.answer} onChange={(e) => setForm((f) => ({ ...f, answer: e.target.value }))} className={inputCls} placeholder="Short paragraphs. Blank lines create spacing." />
          </label>
          <div className="grid gap-4 sm:grid-cols-3">
            <label className="block">
              <span className={labelCls}>Category</span>
              <select data-testid="question-category" value={form.category} onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))} className={inputCls}>
                <option value="">None</option>
                {CATEGORIES.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </label>
            <label className="block">
              <span className={labelCls}>Position</span>
              <select data-testid="question-position" value={form.position} onChange={(e) => setForm((f) => ({ ...f, position: e.target.value }))} className={inputCls}>
                {POSITIONS.map((p) => (
                  <option key={p} value={p}>{p || "None"}</option>
                ))}
              </select>
            </label>
            <label className="block">
              <span className={labelCls}>Sort Order</span>
              <input type="number" data-testid="question-sort" value={form.sort_order} onChange={(e) => setForm((f) => ({ ...f, sort_order: e.target.value }))} className={inputCls} placeholder="Optional" />
            </label>
          </div>
          <label className="flex cursor-pointer items-center gap-3 border border-pine-700/20 bg-paper p-4">
            <input type="checkbox" data-testid="question-published-checkbox" checked={form.published} onChange={(e) => setForm((f) => ({ ...f, published: e.target.checked }))} className="h-5 w-5 accent-pine-700" />
            <span className="text-sm font-medium text-ink/80">Published — visible on /questions (uncheck to unpublish)</span>
          </label>
          <div className="flex flex-wrap gap-3">
            <button onClick={save} disabled={saving} data-testid="question-save-button" className="flex items-center gap-2 bg-pine-700 px-7 py-3.5 text-sm font-semibold text-paper transition-colors hover:bg-terra-500 disabled:opacity-60">
              {saving && <Loader2 className="h-4 w-4 animate-spin" />}
              {editId ? "Save changes" : form.published ? "Publish question" : "Save draft"}
            </button>
            {editId && (
              <button onClick={() => { setEditId(null); setForm(EMPTY); }} data-testid="question-cancel-edit" className="border border-pine-700/25 px-6 py-3.5 text-sm font-semibold text-pine-700 hover:border-terra-500">
                Cancel edit
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="mt-8 space-y-3" data-testid="questions-admin-list">
        {loading ? (
          <div className="flex items-center gap-2 py-10 text-ink/50"><Loader2 className="h-5 w-5 animate-spin" /> Loading…</div>
        ) : items.length === 0 ? (
          <p className="border border-dashed border-pine-700/25 py-10 text-center text-sm text-ink/50">No questions yet. Add your first one above.</p>
        ) : (
          items.map((q) => (
            <div key={q.id} data-testid={`question-row-${q.id}`} className="flex flex-wrap items-center justify-between gap-3 border border-pine-700/15 bg-white/60 px-6 py-4">
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-3">
                  <span className={`px-2.5 py-1 font-mono text-[10px] font-semibold uppercase tracking-[0.15em] ${q.published ? "bg-pine-700 text-paper" : "bg-parch text-ink/60"}`}>
                    {q.published ? "Live" : "Draft"}
                  </span>
                  <p className="font-serif text-base font-semibold tracking-tight">{q.question}</p>
                </div>
                <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.15em] text-ink/45">
                  {[q.position, q.category, q.sort_order != null ? `#${q.sort_order}` : ""].filter(Boolean).join(" · ")}
                </p>
              </div>
              <div className="flex gap-2">
                <button onClick={() => edit(q)} data-testid={`question-edit-${q.id}`} className="flex h-9 w-9 items-center justify-center border border-pine-700/25 text-pine-700 hover:border-pine-700 hover:bg-pine-700 hover:text-paper" aria-label="Edit">
                  <Pencil className="h-4 w-4" />
                </button>
                <button onClick={() => remove(q.id)} data-testid={`question-delete-${q.id}`} className="flex h-9 w-9 items-center justify-center border border-pine-700/25 text-pine-700 hover:border-red-600 hover:bg-red-600 hover:text-white" aria-label="Delete">
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default AdminQuestions;
